import { AbstractControl } from '@angular/forms';

import { WorkflowStepErrorResponse } from '@shared/models';

/**
 * KOR's code for a step whose configuration names a custom field the business account can no longer
 * resolve. It arrives as a 422 with no `errors` list, so it cannot be pinned to a field and is not
 * recoverable by the customer - unlike the 400 field errors below.
 */
export const CUSTOM_FIELD_UNRESOLVED = 'custom-field-unresolved';

/** Where a field error's `path` puts the custom fields: `<stepName>/customFields/<shortName>`. */
const CUSTOM_FIELD_PATH = /(?:^|\/)customFields\/(?<shortName>[^/]+)$/u;

const bodyOf = (errorRes: unknown): WorkflowStepErrorResponse =>
  ((errorRes as { error?: WorkflowStepErrorResponse } | undefined)?.error ?? {}) as WorkflowStepErrorResponse;

export const isCustomFieldUnresolved = (errorRes: unknown): boolean => CUSTOM_FIELD_UNRESOLVED === bodyOf(errorRes).errorCode;

/**
 * KOR's code for a workflow that has already left ACTIVE. On a step submit it means the step this
 * call was completing is done - typically because the server completed it first - rather than that
 * anything failed.
 */
export const WORKFLOW_STATUS_INVALID = 'status-invalid';

export const isWorkflowNoLongerActive = (errorRes: unknown): boolean => WORKFLOW_STATUS_INVALID === bodyOf(errorRes).errorCode;

/**
 * What KOR rejected, by `shortName`.
 *
 * `message` is null on every custom-field error KOR raises, so the code carries the meaning and the
 * wording is ours. A code we do not recognise still yields an entry, because leaving the field
 * unmarked would show the customer a step that submits and fails with nothing highlighted.
 */
const FALLBACK_MESSAGE = 'Enter a valid value';

/**
 * Prototype-free: a code of `constructor` or `toString` would otherwise resolve through Object's
 * prototype and hand the customer a function where the message should be.
 */
const MESSAGE_BY_CODE: Record<string, string> = Object.assign(Object.create(null) as Record<string, string>, {
  required: 'This field is required',
  invalid: FALLBACK_MESSAGE,
  'not-allowed': 'This field is no longer being collected',
});

export const customFieldErrors = (errorRes: unknown): Record<string, string> =>
  (bodyOf(errorRes).errors ?? []).reduce<Record<string, string>>((errors, fieldError) => {
    const shortName = CUSTOM_FIELD_PATH.exec(fieldError.path ?? '')?.groups?.shortName;

    return shortName
      ? { ...errors, [shortName]: fieldError.message ?? MESSAGE_BY_CODE[fieldError.errorCode ?? ''] ?? FALLBACK_MESSAGE }
      : errors;
  }, {});

/**
 * Whether the customer can fix this on the step they are already looking at.
 *
 * Deliberately narrow: everything else stays terminal, because this predicate is what decides whether
 * a shared submit path keeps the customer on the step or sends them to the failure page, and every
 * other step in the workflow goes through the same code.
 */
export const isRecoverableCustomFieldError = (errorRes: unknown): boolean =>
  isCustomFieldUnresolved(errorRes) || Object.keys(customFieldErrors(errorRes)).length > 0;

/**
 * Puts KOR's rejections onto the form so they render where the customer is looking.
 *
 * Marked touched because rocket-ui's field wrapper only shows an error on a touched control, and a
 * server error the customer never saw is worse than no validation at all.
 *
 * @returns how many controls were actually marked. KOR raises `not-allowed` precisely for a field the
 * step does not configure, so there may be no control to mark - and the caller has to be able to tell,
 * or the customer is left on a step that refuses to submit with nothing highlighted and nothing said.
 */
export const applyCustomFieldErrors = (form: AbstractControl | null | undefined, errors: Record<string, string>): number =>
  Object.entries(errors).reduce((marked, [shortName, message]) => {
    const control = form?.get(['customFields', shortName]);

    if (!control) {
      return marked;
    }

    control.setErrors({ ...control.errors, pattern: { message } });
    control.markAsTouched();

    return marked + 1;
  }, 0);
